import { ThemeEditorState } from "../types/editor";

// these are common between light and dark modes
// we can assume that light mode's value will be used for dark mode as well
export const COMMON_STYLES = [
  "font-sans",
  "font-serif",
  "font-mono",
  "radius",
  "shadow-opacity",
  "shadow-blur",
  "shadow-spread",
  "shadow-x",
  "shadow-y",
  "tracking-normal",
  "spacing",
];

export const DEFAULT_FONT_SANS =
  "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'";

export const DEFAULT_FONT_SERIF = 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif';

export const DEFAULT_FONT_MONO =
  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

// Default light theme styles
export const defaultLightThemeStyles = {
  background: "hsl(0 0% 100%)",
  foreground: "hsl(240 10.0000% 3.9216%)",
  card: "hsl(0 0% 100%)",
  "card-foreground": "hsl(240 10.0000% 3.9216%)",
  popover: "hsl(0 0% 100%)",
  "popover-foreground": "hsl(240 10.0000% 3.9216%)",
  primary: "hsl(263.3898 69.9605% 50.3922%)",
  "primary-foreground": "hsl(0 0% 100%)",
  secondary: "hsl(240 4.7619% 95.8824%)",
  "secondary-foreground": "hsl(240 5.8824% 10%)",
  muted: "hsl(240 4.7619% 95.8824%)",
  "muted-foreground": "hsl(240 3.8298% 46.0784%)",
  accent: "hsl(240 4.7619% 95.8824%)",
  "accent-foreground": "hsl(240 5.8824% 10%)",
  destructive: "hsl(0 84.2365% 60.1961%)",
  "destructive-foreground": "hsl(0 0% 98.0392%)",
  border: "hsl(240 5.8824% 90%)",
  input: "hsl(240 5.8824% 90%)",
  ring: "hsl(263.3898 69.9605% 50.3922%)",
  "chart-1": "hsl(263.3898 69.9605% 50.3922%)",
  "chart-2": "hsl(270.7407 91.0112% 65.0980%)",
  "chart-3": "hsl(269.1892 97.3684% 85.0980%)",
  "chart-4": "hsl(262.1229 83.2558% 57.8431%)",
  "chart-5": "hsl(263.5000 67.4157% 34.9020%)",
  radius: "0.75rem",
  sidebar: "hsl(0 0% 98.0392%)",
  "sidebar-foreground": "hsl(240 5.8824% 10%)",
  "sidebar-primary": "hsl(263.3898 69.9605% 50.3922%)",
  "sidebar-primary-foreground": "hsl(0 0% 100%)",
  "sidebar-accent": "hsl(240 4.7619% 95.8824%)",
  "sidebar-accent-foreground": "hsl(240 5.8824% 10%)",
  "sidebar-border": "hsl(240 5.8824% 90%)",
  "sidebar-ring": "hsl(263.3898 69.9605% 50.3922%)",
  "font-sans": 'Inter, sans-serif',
  "font-serif": '"Merriweather", serif',
  "font-mono": '"JetBrains Mono", monospace',

  "shadow-x": "0px",
  "shadow-y": "4px",
  "shadow-blur": "10px",
  "shadow-spread": "0px",
  "shadow-opacity": "0.05",
  "shadow-color": "#000000",
  "shadow-2xs": "0px 4px 10px 0px hsl(0 0% 0% / 0.03)",
  "shadow-xs": "0px 4px 10px 0px hsl(0 0% 0% / 0.03)",
  "shadow-sm": "0px 4px 10px 0px hsl(0 0% 0% / 0.05), 0px 1px 2px -1px hsl(0 0% 0% / 0.05)",
  "shadow": "0px 4px 10px 0px hsl(0 0% 0% / 0.05), 0px 1px 2px -1px hsl(0 0% 0% / 0.05)",
  "shadow-md": "0px 4px 10px 0px hsl(0 0% 0% / 0.05), 0px 2px 4px -1px hsl(0 0% 0% / 0.05)",
  "shadow-lg": "0px 4px 10px 0px hsl(0 0% 0% / 0.05), 0px 4px 6px -1px hsl(0 0% 0% / 0.05)",
  "shadow-xl": "0px 4px 10px 0px hsl(0 0% 0% / 0.05), 0px 8px 10px -1px hsl(0 0% 0% / 0.05)",
  "shadow-2xl": "0px 4px 10px 0px hsl(0 0% 0% / 0.13)",

  "tracking-normal": "-0.01em",
  spacing: "0.25rem",
};

// Default dark theme styles
export const defaultDarkThemeStyles = {
  ...defaultLightThemeStyles,
  background: "hsl(240 40.5405% 7.2549%)",
  foreground: "hsl(210.0000 40.0000% 98.0392%)",
  card: "hsl(240 40.9836% 11.9608%)",
  "card-foreground": "hsl(210.0000 40.0000% 98.0392%)",
  popover: "hsl(240 40.5405% 7.2549%)",
  "popover-foreground": "hsl(210.0000 40.0000% 98.0392%)",
  primary: "hsl(258.3117 89.5349% 66.2745%)",
  "primary-foreground": "hsl(0 0% 100%)",
  secondary: "hsl(240 35.4839% 18.2353%)",
  "secondary-foreground": "hsl(210.0000 40.0000% 98.0392%)",
  muted: "hsl(240 35.4839% 18.2353%)",
  "muted-foreground": "hsl(215.0000 20.2247% 65.0980%)",
  accent: "hsl(240 33.3333% 26.4706%)",
  "accent-foreground": "hsl(0 0% 100%)",
  destructive: "hsl(0 62.8205% 30.5882%)",
  "destructive-foreground": "hsl(210.0000 40.0000% 98.0392%)",
  border: "hsl(240 33.3333% 26.4706%)",
  input: "hsl(240 33.3333% 26.4706%)",
  ring: "hsl(258.3117 89.5349% 66.2745%)",
  "chart-1": "hsl(258.3117 89.5349% 66.2745%)",
  "chart-2": "hsl(270 95.2381% 75.2941%)",
  "chart-3": "hsl(269.1892 97.3684% 85.0980%)",
  "chart-4": "hsl(262.1229 83.2558% 57.8431%)",
  "chart-5": "hsl(263.5000 67.4157% 34.9020%)",
  radius: "0.75rem",
  sidebar: "hsl(240 40.5405% 7.2549%)",
  "sidebar-foreground": "hsl(210.0000 40.0000% 98.0392%)",
  "sidebar-primary": "hsl(258.3117 89.5349% 66.2745%)",
  "sidebar-primary-foreground": "hsl(0 0% 100%)",
  "sidebar-accent": "hsl(240 35.4839% 18.2353%)",
  "sidebar-accent-foreground": "hsl(210.0000 40.0000% 98.0392%)",
  "sidebar-border": "hsl(240 33.3333% 26.4706%)",
  "sidebar-ring": "hsl(258.3117 89.5349% 66.2745%)",
  "font-sans": '"Plus Jakarta Sans", sans-serif',
  "font-serif": '"Georgia", serif',
  "font-mono": '"Fira Code", monospace',

  "shadow-x": "0px",
  "shadow-y": "10px",
  "shadow-blur": "20px",
  "shadow-spread": "0px",
  "shadow-opacity": "0.4",
  "shadow-color": "#000000",
  "shadow-2xs": "0px 10px 20px 0px hsl(0 0% 0% / 0.20)",
  "shadow-xs": "0px 10px 20px 0px hsl(0 0% 0% / 0.20)",
  "shadow-sm": "0px 10px 20px 0px hsl(0 0% 0% / 0.40), 0px 1px 2px -1px hsl(0 0% 0% / 0.40)",
  "shadow": "0px 10px 20px 0px hsl(0 0% 0% / 0.40), 0px 1px 2px -1px hsl(0 0% 0% / 0.40)",
  "shadow-md": "0px 10px 20px 0px hsl(0 0% 0% / 0.40), 0px 2px 4px -1px hsl(0 0% 0% / 0.40)",
  "shadow-lg": "0px 10px 20px 0px hsl(0 0% 0% / 0.40), 0px 4px 6px -1px hsl(0 0% 0% / 0.40)",
  "shadow-xl": "0px 10px 20px 0px hsl(0 0% 0% / 0.40), 0px 8px 10px -1px hsl(0 0% 0% / 0.40)",
  "shadow-2xl": "0px 10px 20px 0px hsl(0 0% 0% / 1.00)",

  "tracking-normal": "-0.01em",
  spacing: "0.25rem",
};

// Default theme state
export const defaultThemeState: ThemeEditorState = {
  styles: {
    light: defaultLightThemeStyles,
    dark: defaultDarkThemeStyles,
  },
  currentMode:
    typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light",
  hslAdjustments: {
    hueShift: 0,
    saturationScale: 1,
    lightnessScale: 1,
  },
};
